// LocationPrimer — central permission priming system for habano.app.
//
// Rule: the real browser geolocation prompt MUST never fire automatically.
// Every call to navigator.geolocation.getCurrentPosition is funnelled through
// requestLocation(context) which:
//   1. If permission already 'granted' → resolves immediately with cached or
//      live coords (no card, no prompt).
//   2. If the user has tapped "Not now" before (habano.location.denied flag)
//      OR the browser permission state is 'denied' → no card, resolves with
//      { ok: false, reason: "denied" } so the caller can show its own
//      inline fallback. A toast may also surface the revisit hint.
//   3. Otherwise → shows the branded LocationPermissionCard with the
//      context-specific copy. On "Enable" → fires the browser prompt and
//      resolves with the outcome. On "Not now" → sets the localStorage
//      flag and resolves with { ok: false, reason: "soft-denied" }.
//
// Usage:
//   const { requestLocation } = useLocationPrimer();
//   const res = await requestLocation("ride");
//   if (res.ok) { ...use res.lat, res.lng... }
//
// Contexts: "map" | "ride" | "airport" | "navigate" | "distance"
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { MapPin, Car, Plane, Navigation, Locate, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "./i18n";

const LS_DENIED = "habano.location.denied";
const LS_PROMPTED = "habano.location.prompted";
const LS_GPS_CACHE = "habano.gps.last";

// Context → icon + i18n key map. Each entry holds the headline + body + the
// label for the secondary "Not now" link (CTA label is shared across all).
const CONTEXTS = {
  map:      { icon: MapPin,     keys: { h: "loc_map_headline",      b: "loc_map_body",      s: "loc_map_secondary"      } },
  ride:     { icon: Car,        keys: { h: "loc_ride_headline",     b: "loc_ride_body",     s: "loc_ride_secondary"     } },
  airport:  { icon: Plane,      keys: { h: "loc_airport_headline",  b: "loc_airport_body",  s: "loc_airport_secondary"  } },
  navigate: { icon: Navigation, keys: { h: "loc_navigate_headline", b: "loc_navigate_body", s: "loc_navigate_secondary" } },
  distance: { icon: Locate,     keys: { h: "loc_distance_headline", b: "loc_distance_body", s: "loc_distance_secondary" } },
};

const Ctx = createContext(null);

function readGpsCache() {
  try {
    const raw = localStorage.getItem(LS_GPS_CACHE);
    if (!raw) return null;
    const v = JSON.parse(raw);
    if (!v || typeof v.lat !== "number" || typeof v.lng !== "number") return null;
    // 7-day cache, same window as the legacy useGps
    if (Date.now() - (v.ts || 0) > 1000 * 60 * 60 * 24 * 7) return null;
    return { lat: v.lat, lng: v.lng };
  } catch { /* ignore corrupt JSON */ return null; }
}
function writeGpsCache(lat, lng) {
  try { localStorage.setItem(LS_GPS_CACHE, JSON.stringify({ lat, lng, ts: Date.now() })); } catch { /* storage quota / private mode */ }
}

function isIos() {
  if (typeof navigator === "undefined") return false;
  return /iPhone|iPad|iPod/i.test(navigator.userAgent || "");
}

// Native call to the browser API. Resolves either { ok:true, lat, lng } or
// { ok:false, reason:"denied"|"timeout"|"unsupported"|"unknown" }.
function fireBrowserPrompt() {
  return new Promise((resolve) => {
    if (typeof navigator === "undefined" || !("geolocation" in navigator)) {
      resolve({ ok: false, reason: "unsupported" }); return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        writeGpsCache(lat, lng);
        resolve({ ok: true, lat, lng });
      },
      (err) => resolve({
        ok: false,
        reason: err && err.code === 1 ? "denied" : "unknown",
      }),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
    );
  });
}

export function LocationPrimerProvider({ children }) {
  const { t } = useI18n();
  // visible: null | { context, resolve }
  const [visible, setVisible] = useState(null);
  const [busy, setBusy] = useState(false);
  // Browser permission state (granted | denied | prompt | null). null = unknown
  // (browser doesn't expose Permissions API for geolocation, e.g. Safari old).
  const [permState, setPermState] = useState(null);
  const resolveRef = useRef(null);

  // Probe the Permissions API once on mount so we can short-circuit when the
  // browser has already granted/denied access without ever showing the card.
  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.permissions || !navigator.permissions.query) return;
    let cancelled = false;
    navigator.permissions.query({ name: "geolocation" }).then((p) => {
      if (cancelled) return;
      setPermState(p.state);
      p.onchange = () => setPermState(p.state);
    }).catch(() => { /* Permissions API may reject in private mode */ });
    return () => { cancelled = true; };
  }, []);

  const showDeniedHint = useCallback(() => {
    const hint = isIos() ? t("loc_denied_ios_hint") : t("loc_denied_android_hint");
    toast.error(`${t("loc_denied_toast")}. ${hint}`, { duration: 6000 });
  }, [t]);

  const requestLocation = useCallback((context = "distance") => new Promise((resolve) => {
    // 1. Already granted in the browser → resolve immediately with cache or
    //    fresh fix. Never show the card.
    if (permState === "granted") {
      const cached = readGpsCache();
      if (cached) { resolve({ ok: true, ...cached }); return; }
      fireBrowserPrompt().then(resolve);
      return;
    }
    // 2. User previously tapped "Not now" → no card. Surface the revisit
    //    hint so the user knows how to undo their choice if they want.
    const softDenied = (() => { try { return localStorage.getItem(LS_DENIED) === "1"; } catch { return false; } })();
    if (softDenied || permState === "denied") {
      toast.message(t("loc_denied_revisit"), { duration: 5000 });
      resolve({ ok: false, reason: permState === "denied" ? "denied" : "soft-denied" });
      return;
    }
    // 3. Otherwise → show the priming card and wait for the user's choice.
    resolveRef.current = resolve;
    try { localStorage.setItem(LS_PROMPTED, "1"); } catch { /* ignore */ }
    setVisible({ context: CONTEXTS[context] ? context : "distance" });
  }), [permState, t]);

  const onEnable = useCallback(async () => {
    setBusy(true);
    const res = await fireBrowserPrompt();
    setBusy(false);
    setVisible(null);
    if (!res.ok && res.reason === "denied") {
      try { localStorage.setItem(LS_DENIED, "1"); } catch { /* ignore */ }
      showDeniedHint();
    }
    if (resolveRef.current) { resolveRef.current(res); resolveRef.current = null; }
  }, [showDeniedHint]);

  const onCancel = useCallback(() => {
    try { localStorage.setItem(LS_DENIED, "1"); } catch { /* ignore */ }
    setVisible(null);
    if (resolveRef.current) {
      resolveRef.current({ ok: false, reason: "soft-denied" });
      resolveRef.current = null;
    }
  }, []);

  // Allow other code to know whether GPS will work without a prompt.
  const isGranted = permState === "granted";
  // Re-derive cached coords on each render so callers can read them
  // synchronously when they already have permission.
  const cachedCoords = isGranted ? readGpsCache() : null;

  const ctxValue = { requestLocation, isGranted, permState, cachedCoords };
  const ctx = visible ? CONTEXTS[visible.context] : null;
  const Icon = ctx?.icon || MapPin;

  return (
    <Ctx.Provider value={ctxValue}>
      {children}
      {visible && (
        <div
          data-testid="location-primer-overlay"
          className="fixed inset-0 z-[200] flex items-center justify-center px-5"
          style={{ background: "rgba(0,0,0,0.85)" }}
          role="dialog"
          aria-modal="true"
        >
          <div
            data-testid={`location-primer-card-${visible.context}`}
            className="w-full max-w-md text-center"
            style={{
              background: "#171717",
              border: "1px solid rgba(232,184,75,0.3)",
              borderRadius: 20,
              padding: "32px 24px",
            }}
          >
            <div className="flex justify-center mb-5">
              <Icon style={{ width: 48, height: 48, color: "#E8B84B" }} strokeWidth={1.5} />
            </div>
            <h2
              data-testid="location-primer-headline"
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: 24,
                color: "#F5F1E8",
                lineHeight: 1.25,
                marginBottom: 12,
              }}
            >
              {t(ctx.keys.h)}
            </h2>
            <p
              data-testid="location-primer-body"
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 16,
                color: "#B0AAA3",
                lineHeight: 1.5,
                marginBottom: 24,
              }}
            >
              {t(ctx.keys.b)}
            </p>
            <button
              data-testid="location-primer-enable"
              onClick={onEnable}
              disabled={busy}
              className="w-full inline-flex items-center justify-center gap-2 disabled:opacity-60"
              style={{
                height: 52,
                background: "#E8B84B",
                color: "#0B0B0B",
                fontWeight: 600,
                borderRadius: 14,
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 16,
              }}
            >
              {busy ? <Loader2 className="w-5 h-5 animate-spin" strokeWidth={2} /> : null}
              {t("loc_cta_enable")}
            </button>
            <button
              data-testid="location-primer-dismiss"
              onClick={onCancel}
              disabled={busy}
              className="block mx-auto mt-4 disabled:opacity-60"
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 14,
                color: "#9A9490",
                textDecoration: "none",
                minHeight: 48,
                padding: "8px 16px",
              }}
            >
              {t(ctx.keys.s)}
            </button>
          </div>
        </div>
      )}
    </Ctx.Provider>
  );
}

export function useLocationPrimer() {
  const ctx = useContext(Ctx);
  if (!ctx) {
    // Defensive fallback so callers never crash if mounted outside the
    // provider tree (e.g. isolated tests). Returns a no-op that mirrors the
    // "denied" branch so the UI degrades gracefully.
    return {
      requestLocation: async () => ({ ok: false, reason: "no-provider" }),
      isGranted: false,
      permState: null,
      cachedCoords: null,
    };
  }
  return ctx;
}
